import { useContext } from 'react'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCutterProfileModule, { useCutterProfileModuleForm } from '@hooks/useCutterProfileModule'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditCutterProfileModuleModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCutterProfileModule: (payload: HandleCallbackActionParams) => void,
  updateCutterProfileModule: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCutterProfileModuleModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCutterProfileModule, updateCutterProfileModule } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const cutterProfileModule = digObject(modalPayload, 'cutterProfileModule', {})

  const newRecord = !cutterProfileModule.id

  const {
    callbacks: {
      createCutterProfileModule: createFn,
      updateCutterProfileModule: updateFn,
    },
    creating,
    updating,
  } = useCutterProfileModule(cutterProfileModule)

  const {
    entityState,
    entityState: {
      name,
      description,
      topLeftXCoordinates,
      topLeftYCoordinates,
      width,
      height,
      adTypeId,
      instances,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useCutterProfileModuleForm(cutterProfileModule, { validateOn: 'change' })
  const createOrUpdateCutterProfileModulePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Cutter Profile Module ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Cutter Profile Module" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Name" errorMessage={errors.name}>
            <input name="name" type="text" value={name} {...handlers} />
          </Form.Field>
          <Form.Field label="Description" errorMessage={errors.description} marginTop="large">
            <textarea style={{ height: 120, resize: 'vertical' }} name="description" value={description} {...handlers} />
          </Form.Field>
          <Form.Field label="Ad Type" marginTop="large">
            <select onChange={e => setEntityState({ adTypeId: e.target.value })} value={adTypeId}>
              <option>None</option>
              <option value="0">Google Display</option>
              <option value="1">Facebook</option>
              <option value="2">Google Search</option>
            </select>
          </Form.Field>

          <SectionDivider marginTop="large" marginBottom="large">Offset</SectionDivider>

          <Form.Field label="* Top Left X Coordinates" errorMessage={errors.topLeftXCoordinates} marginTop="large">
            <input name="topLeftXCoordinates" type="number" value={topLeftXCoordinates} {...handlers} />
          </Form.Field>
          <Form.Field label="* Top Left Y Coordinates" errorMessage={errors.topLeftYCoordinates} marginTop="large">
            <input name="topLeftYCoordinates" type="number" value={topLeftYCoordinates} {...handlers} />
          </Form.Field>

          <SectionDivider marginTop="large" marginBottom="large">Dimensions</SectionDivider>

          <Form.Field label="* Width" errorMessage={errors.width} marginTop="large">
            <input name="width" type="number" value={width} {...handlers} />
          </Form.Field>
          <Form.Field label="* Height" errorMessage={errors.height} marginTop="large">
            <input name="height" type="number" value={height} {...handlers} />
          </Form.Field>

          <SectionDivider marginTop="large" marginBottom="large">Render Configuration</SectionDivider>
          <Form.Field label="Instances" errorMessage={errors.instances} marginTop="large">
            <input name="instances" type="number" value={instances} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCutterProfileModule(createOrUpdateCutterProfileModulePayload)
              : () => updateCutterProfileModule(createOrUpdateCutterProfileModulePayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Cutter Profile Module` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCutterProfileModuleModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
